import React, { useState } from 'react';
import PropTypes from 'prop-types';
import notify from '../../../utils/notify';

import { PlusCircle, Pencil, Trash2, Lock } from 'lucide-react';
import ConfirmModal from '../../utils/ConfirmModal';

import '../../../component-styles/TimeBillingInvoiceView.css';
import API_BASE from '../../../utils/api.js';
import { roundCurrency, sanitizeCurrencyInput } from '../../../utils/currency.js';
import usePermissions from '../../../auth/usePermissions';

function formatCurrency(v) {
  if (v == null || Number.isNaN(Number(v))) return '$0.00';
  return Number(v).toLocaleString(undefined, {
    style: 'currency',
    currency: 'USD',
  });
}

const QB_SYNC_SKIP_REASONS = {
  missing_bill_sync: 'this purchase order has not been synced to QuickBooks yet',
  missing_vendor_mapping: 'no QuickBooks vendor is linked to this purchase order',
  missing_bank_account_mapping: 'no QuickBooks bank account is configured for payments',
  missing_credit_card_account: 'no QuickBooks credit card account is configured for payments',
  ambiguous_credit_card_account:
    "multiple QuickBooks credit card accounts exist and the payment reference didn't identify which card was used — include the card's last 4 digits in the Reference field",
  invalid_amount: 'the payment amount could not be applied to the QuickBooks bill',
  sync_in_progress: 'a QuickBooks sync for this purchase order was already running',
};

function getQuickBooksSyncWarning(quickbooksPayment) {
  const status = quickbooksPayment?.status;
  if (!status || status === 'created') return null;
  if (status === 'failed') {
    return `Payment recorded, but syncing it to QuickBooks failed: ${
      quickbooksPayment.error || 'unknown error'
    }. It will retry the next time this purchase order is synced to QuickBooks.`;
  }
  if (status === 'skipped' && quickbooksPayment.reason === 'before_sync_start_date') {
    return 'Payment recorded. It is dated before your QuickBooks sync start date, so it was not sent to QuickBooks.';
  }
  if (status === 'skipped') {
    const reason = QB_SYNC_SKIP_REASONS[quickbooksPayment.reason] || 'it did not sync automatically';
    return `Payment recorded, but not yet reflected in QuickBooks because ${reason}. It will sync the next time this purchase order is synced to QuickBooks.`;
  }
  return null;
}

const MANUAL_METHOD_OPTIONS = [
  { value: 'bank_transfer', label: 'Bank Transfer' },
  { value: 'cash', label: 'Cash' },
  { value: 'check', label: 'Check' },
  { value: 'credit_card', label: 'Credit Card' },
  { value: 'venmo', label: 'Venmo' },
  { value: 'zelle', label: 'Zelle' },
  { value: 'ach', label: 'ACH' },
  { value: 'wire_transfer', label: 'Wire Transfer' },
  { value: 'other', label: 'Other' },
];

export default function PurchaseOrderPayments({ purchaseOrder, projectId, onUpdated }) {
  const { permissions } = usePermissions();
  const canApplyPayments = permissions.createEditPurchaseOrders !== false;

  const userId = localStorage.getItem('userId') || purchaseOrder?.userId || '';
  const token = localStorage.getItem('idToken');

  const total = roundCurrency(purchaseOrder?.total || 0);
  const paidFromPayments = roundCurrency(purchaseOrder?.payments || 0);
  const paidFromHistory = (purchaseOrder.paymentHistory || []).reduce(
    (sum, p) => sum + roundCurrency(p?.amount || 0),
    0
  );
  const paid = roundCurrency(Math.max(paidFromPayments, paidFromHistory));
  const remainingBalance = roundCurrency(Math.max(total - paid, 0));

  const [showManual, setShowManual] = useState(false);
  const [expandedPaymentId, setExpandedPaymentId] = useState(null);

  const [manualForm, setManualForm] = useState({
    amount: '',
    method: 'bank_transfer',
    paymentDate: new Date().toISOString().slice(0, 10),
    reference: '',
  });

  const [editPayment, setEditPayment] = useState(null);
  const [editForm, setEditForm] = useState({ amount: '', method: 'bank_transfer', paymentDate: '', reference: '' });
  const [confirmDelete, setConfirmDelete] = useState(null);

  async function applyManualPayment() {
    const amt = roundCurrency(manualForm.amount);
    if (!amt || amt <= 0) return;

    const res = await fetch(
      `${API_BASE}/api/purchase-orders/${projectId}/${purchaseOrder.poId}/apply-payment`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          userid: userId,
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          userId,
          amount: amt,
          paymentType: 'manual',
          method: manualForm.method,
          paymentDate: manualForm.paymentDate,
          reference: manualForm.reference,
        }),
      }
    );

    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      notify(data.error || 'Failed to record payment');
      return;
    }

    setShowManual(false);
    setManualForm({
      amount: '',
      method: 'bank_transfer',
      paymentDate: new Date().toISOString().slice(0, 10),
      reference: '',
    });
    const qbWarning = getQuickBooksSyncWarning(data?.quickbooksPayment);
    if (qbWarning) notify.warning(qbWarning);
    onUpdated?.();
  }

  function openEdit(p) {
    setEditPayment(p);
    setEditForm({
      amount: String(p.amount),
      method: p.method || p.paymentType || 'bank_transfer',
      paymentDate: p.date ? p.date.slice(0, 10) : new Date().toISOString().slice(0, 10),
      reference: p.reference || '',
    });
  }

  async function saveEdit() {
    const amt = roundCurrency(editForm.amount);
    if (!amt || amt <= 0) return;

    const res = await fetch(
      `${API_BASE}/api/purchase-orders/${projectId}/${purchaseOrder.poId}/payments/${editPayment.paymentId}`,
      {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          userid: userId,
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          amount: amt,
          method: editForm.method,
          reference: editForm.reference,
          paymentDate: editForm.paymentDate,
        }),
      }
    );

    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      notify(data.error || 'Failed to update payment');
      return;
    }

    setEditPayment(null);
    const qbWarning = getQuickBooksSyncWarning(data?.quickbooksPayment);
    if (qbWarning) notify.warning(qbWarning.replace('Payment recorded', 'Payment updated'));
    onUpdated?.();
  }

  async function deletePayment(paymentId) {
    const res = await fetch(
      `${API_BASE}/api/purchase-orders/${projectId}/${purchaseOrder.poId}/payments/${paymentId}`,
      {
        method: 'DELETE',
        headers: {
          userid: userId,
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      }
    );

    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      notify(data.error || 'Failed to delete payment');
      return;
    }

    onUpdated?.();
  }

  return (
    <div className="rd-payments-section">
      <h3>Payments</h3>

      {purchaseOrder.paymentHistory?.length === 0 && (
        <div className="rd-empty">No payments yet.</div>
      )}

      {purchaseOrder.paymentHistory?.length > 0 && (
        <table className="rd-payments-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Method</th>
              <th>Reference</th>
              <th>Amount</th>
              {canApplyPayments && <th></th>}
            </tr>
          </thead>
          <tbody>
            {purchaseOrder.paymentHistory.map((p) => {
              const isExpanded = expandedPaymentId === p.paymentId;
              const paidToDate = purchaseOrder.paymentHistory
                .slice(
                  0,
                  purchaseOrder.paymentHistory.findIndex((x) => x.paymentId === p.paymentId) + 1
                )
                .reduce((sum, entry) => sum + Number(entry.amount || 0), 0);
              const remainingAfter = Math.max(total - paidToDate, 0);
              return (
                <React.Fragment key={p.paymentId}>
                  <tr
                    className="rd-payment-row"
                    onClick={() => setExpandedPaymentId(isExpanded ? null : p.paymentId)}
                  >
                    <td>{p.date?.slice(0, 10)}</td>
                    <td>{p.method || p.paymentType}</td>
                    <td className="rd-ref-cell">{p.reference || '-'}</td>
                    <td>{formatCurrency(p.amount)}</td>
                    {canApplyPayments && (
                      <td onClick={(e) => e.stopPropagation()} style={{ whiteSpace: 'nowrap' }}>
                        {p.paymentType === 'stripe' ? (
                          <Lock size={13} style={{ color: '#aaa', verticalAlign: 'middle' }} title="Stripe payment — locked" />
                        ) : (
                          <>
                            <button
                              className="rd-icon-btn"
                              title="Edit payment"
                              onClick={() => openEdit(p)}
                              style={{ marginRight: 4 }}
                            >
                              <Pencil size={13} />
                            </button>
                            <button
                              className="rd-icon-btn rd-icon-btn--danger"
                              title="Delete payment"
                              onClick={() => setConfirmDelete(p)}
                            >
                              <Trash2 size={13} />
                            </button>
                          </>
                        )}
                      </td>
                    )}
                  </tr>
                  {isExpanded && (
                    <tr className="rd-payment-detail-row">
                      <td colSpan={canApplyPayments ? 5 : 4}>
                        <div className="rd-payment-details">
                          <span>Payment ID: {p.paymentId || '-'}</span>
                          <span>Recorded at: {p.date ? new Date(p.date).toLocaleString() : '-'}</span>
                          <span>Type: {p.paymentType || '-'}</span>
                          <span>Credit ID: {p.creditId || '-'}</span>
                          <span>Retainer ID: {p.retainerId || '-'}</span>
                          <span>Running balance: {formatCurrency(remainingAfter)}</span>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      )}

      {remainingBalance > 0 && canApplyPayments && (
        <button className="rd-pay-btn no-pdf" onClick={() => setShowManual(true)}>
          <PlusCircle size={16} style={{ marginRight: 6 }} />
          Make a Payment
        </button>
      )}

      {canApplyPayments && showManual && (
        <div className="rd-overlay payment-popup-overlay" onClick={() => setShowManual(false)}>
          <div className="rd-modal payment-popup-panel" onClick={(e) => e.stopPropagation()}>
            <h3>Record Manual Payment</h3>

            <label>Amount</label>
            <input
              type="number"
              step="0.01"
              value={manualForm.amount}
              onChange={(e) =>
                setManualForm({ ...manualForm, amount: sanitizeCurrencyInput(e.target.value) })
              }
            />

            <label>Method</label>
            <select
              value={manualForm.method}
              onChange={(e) => setManualForm({ ...manualForm, method: e.target.value })}
            >
              {MANUAL_METHOD_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>

            <label>Payment Date</label>
            <input
              type="date"
              value={manualForm.paymentDate}
              onChange={(e) => setManualForm({ ...manualForm, paymentDate: e.target.value })}
            />

            <label>Reference</label>
            <input
              type="text"
              value={manualForm.reference}
              onChange={(e) => setManualForm({ ...manualForm, reference: e.target.value })}
            />

            <div className="rd-modal-actions">
              <button className="rd-cancel" onClick={() => setShowManual(false)}>
                Cancel
              </button>
              <button className="rd-save" onClick={applyManualPayment}>
                Save Payment
              </button>
            </div>
          </div>
        </div>
      )}

      {editPayment && (
        <div className="rd-overlay payment-popup-overlay" onClick={() => setEditPayment(null)}>
          <div className="rd-modal payment-popup-panel" onClick={(e) => e.stopPropagation()}>
            <h3>Edit Payment</h3>

            <label>Amount</label>
            <input
              type="number"
              step="0.01"
              value={editForm.amount}
              onChange={(e) => setEditForm({ ...editForm, amount: sanitizeCurrencyInput(e.target.value) })}
            />

            <label>Method</label>
            <select
              value={editForm.method}
              onChange={(e) => setEditForm({ ...editForm, method: e.target.value })}
            >
              {MANUAL_METHOD_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>

            <label>Payment Date</label>
            <input
              type="date"
              value={editForm.paymentDate}
              onChange={(e) => setEditForm({ ...editForm, paymentDate: e.target.value })}
            />

            <label>Reference</label>
            <input
              type="text"
              value={editForm.reference}
              onChange={(e) => setEditForm({ ...editForm, reference: e.target.value })}
            />

            <div className="rd-modal-actions">
              <button className="rd-cancel" onClick={() => setEditPayment(null)}>Cancel</button>
              <button className="rd-save" onClick={saveEdit}>Save Changes</button>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={!!confirmDelete}
        title="Delete Payment"
        message={`Delete this ${confirmDelete?.method || 'manual'} payment of ${formatCurrency(confirmDelete?.amount)}? This cannot be undone.`}
        confirmLabel="Delete"
        destructive
        onConfirm={() => {
          const id = confirmDelete.paymentId;
          setConfirmDelete(null);
          deletePayment(id);
        }}
        onCancel={() => setConfirmDelete(null)}
      />
    </div>
  );
}

PurchaseOrderPayments.propTypes = {
  purchaseOrder: PropTypes.object.isRequired,
  projectId: PropTypes.string.isRequired,
  onUpdated: PropTypes.func,
};
